import { useState, useEffect } from 'react';
import { ProfileUpdateInput, Profile } from '@/services/profileService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import SkillsInput from '../ui/skills-input';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData: Profile;
  onSave: (data: ProfileUpdateInput) => void;
  isLoading?: boolean;
}

const EditProfileModal = ({ isOpen, onClose, profileData, onSave, isLoading }: EditProfileModalProps) => {
  const [formData, setFormData] = useState<ProfileUpdateInput>(() => ({
    id: profileData?.id || '', // Add null check
    username: profileData?.username || '',
    title: profileData?.title || '',
    location: profileData?.location || '',
    bio: profileData?.bio || '',
    skills: profileData?.skills || [], // Ensure this defaults to an empty array
    linkedin: profileData?.linkedin || '',
    github: profileData?.github || '',
    x: profileData?.x || '',
    website: profileData?.website || ''
  }));

  // Update the useEffect to handle null/undefined skills
  useEffect(() => {
    setFormData({
      id: profileData?.id || '',
      username: profileData?.username || '',
      title: profileData?.title || '',
      location: profileData?.location || '',
      bio: profileData?.bio || '',
      skills: profileData?.skills || [], // Ensure this defaults to an empty array
      linkedin: profileData?.linkedin || '',
      github: profileData?.github || '',
      x: profileData?.x || '',
      website: profileData?.website || ''
    });
  }, [profileData]);

  const [errors, setErrors] = useState<Partial<Record<keyof ProfileUpdateInput, string>>>({});

  // Update form data when profile data changes
  useEffect(() => {
    setFormData({
      username: profileData.username || '',
      title: profileData.title || '',
      location: profileData.location || '',
      bio: profileData.bio || '',
      skills: profileData.skills || [],
      linkedin: profileData.linkedin || '',
      github: profileData.github || '',
      x: profileData.x || '',
      website: profileData.website || ''
    });
  }, [profileData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setErrors(prev => ({ ...prev, [name]: '' })); // Clear error when input changes
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSkillsChange = (newSkills: string[]) => {
    setFormData(prev => ({
      ...prev,
      skills: newSkills
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ProfileUpdateInput, string>> = {};

    if (!formData.username?.trim()) {
      newErrors.username = 'Name is required';
    }

    if (formData.website && !formData.website.match(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/)) {
      newErrors.website = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    // Filter out empty strings and null values
    const cleanedData = Object.entries(formData).reduce((acc, [key, value]) => {
      if (value !== '' && value !== null && (!Array.isArray(value) || value.length > 0)) {
        acc[key] = value;
      }
      return acc;
    }, {} as ProfileUpdateInput);

    onSave(cleanedData);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[525px] w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Make changes to your profile information
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="username">Name *</Label>
              <Input
                id="username"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                className={errors.username ? 'border-destructive' : ''}
              />
              {errors.username && (
                <p className="text-sm text-destructive">{errors.username}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
            />
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">About</Label>
            <Textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleInputChange}
              rows={3}
            />
          </div>

          {/* Skills */}
          <div className="space-y-2">
            <Label htmlFor="skills">Skills</Label>
            <SkillsInput
              skills={formData.skills || []}
              onChange={handleSkillsChange}
            />
            <p className="text-xs text-muted-foreground">
              Press Enter or comma to add a skill, backspace to remove the last skill
            </p>
          </div>

          {/* Social Links */}
          <div className="space-y-4">
            <Label>Social Links</Label>
            <div className="grid grid-cols-1 gap-4">
              <Input
                placeholder="LinkedIn username"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleInputChange}
              />
              <Input
                placeholder="GitHub username"
                name="github"
                value={formData.github}
                onChange={handleInputChange}
              />
              <Input
                placeholder="Twitter username"
                name="x"
                value={formData.x}
                onChange={handleInputChange}
              />
              <Input
                placeholder="Website URL"
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                className={errors.website ? 'border-destructive' : ''}
              />
              {errors.website && (
                <p className="text-sm text-destructive">{errors.website}</p>
              )}
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <span>Saving...</span>
                  <div className="animate-spin h-4 w-4 border-2 border-current rounded-full border-t-transparent" />
                </div>
              ) : (
                'Save changes'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditProfileModal;